import { Lock, LockOpen, TriangleAlert } from "lucide-react";
import { Button } from "../../components/ui/Button";
import { formatTime } from "../../lib/format";
import type { LockState } from "./useSoftLock";

export function LockNotice({
  state,
  onTakeOver,
  onRetry,
}: {
  state: LockState;
  onTakeOver: () => void;
  onRetry: () => void;
}) {
  if (state.status === "blocked") {
    const { holder, lockedAt, stale } = state.lock;
    return (
      <div
        role="status"
        className="flex items-center gap-3 rounded-lg border border-orange/50 bg-orange-wash px-3 py-2 text-sm text-orange-ink"
      >
        {stale ? <LockOpen size={16} aria-hidden="true" /> : <Lock size={16} aria-hidden="true" />}
        <p className="flex-1">
          <strong className="font-semibold">{holder.name}</strong> is editing this document
          {stale
            ? `, but there has been no activity for more than 90 seconds (last seen ${formatTime(lockedAt)}).`
            : ` since ${formatTime(lockedAt)}. You can read it; editing opens up when they finish.`}
        </p>
        {stale ? (
          <Button size="sm" onClick={onTakeOver}>
            Take over editing
          </Button>
        ) : null}
      </div>
    );
  }
  if (state.status === "lost") {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-lg border border-danger/40 bg-danger-wash px-3 py-2 text-sm text-danger"
      >
        <TriangleAlert size={16} aria-hidden="true" />
        <p className="flex-1">
          Your edit lock was taken over or expired. Editing is paused; your unsaved changes are kept on this device.
        </p>
        <Button size="sm" onClick={onRetry}>
          Edit again
        </Button>
      </div>
    );
  }
  if (state.status === "error") {
    return (
      <div
        role="alert"
        className="flex items-center gap-3 rounded-lg border border-danger/40 bg-danger-wash px-3 py-2 text-sm text-danger"
      >
        <TriangleAlert size={16} aria-hidden="true" />
        <p className="flex-1">{state.message}</p>
        <Button size="sm" onClick={onRetry}>
          Try again
        </Button>
      </div>
    );
  }
  return null;
}
